import { Component, inject, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  NavigationEnd,
  NavigationStart,
  Router,
  RouterLink,
  RouterOutlet,
} from '@angular/router';
import { HttpClient } from '@angular/common/http';
import {
  ModalSizes,
  MotifModal,
  MotifModalModule,
  MotifThemeModule,
} from '@ey-xd/ng-motif';
import { CommonModule } from '@angular/common';
import {
  TranslateService,
  TranslatePipe,
  TranslateDirective,
} from '@ngx-translate/core';
import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalModule,
  MsalService,
} from '@azure/msal-angular';
import { filter, Subject, takeUntil } from 'rxjs';
import {
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
} from '@azure/msal-browser';

import { HeaderComponent } from './components/shared/header/header.component';
import { AppInsightsService } from './services/app-insights.service';
import { UserService } from './services/user.service';
import { environment } from '../environments/environment';
import { CommonService } from './services/common.service';
import { ProgressLoaderComponent } from './components/shared/progress-loader/progress-loader.component';
import { DataLoaderComponent } from './components/shared/data-loader/data-loader.component';
import { SecureStorageService } from './services/securestorage.service';
import { TimeoutDialogComponent } from './components/shared/timeout-dialog/timeout-dialog.component';
import { SideNavComponent } from '@shared/side-nav/side-nav.component';
import { CreateUserCommand, UpdateUserCommandRequest } from '@models/userDto';
import { PrivacyComponentComponent } from '@shared/privacy-component/privacy-component.component';
import { LogoutComponent } from '@components/logout/logout.component';
import { HideShowScreenService } from '@services/hide-show-screen.service';
import { ApplicationConstants } from '@constants/app.constants';
import { CacheService } from '@services/cache.service';
import { NotificationService } from '@services/notification.service';
import { IporPrivacyPolicyComponent } from '@shared/ipor-privacy-policy/ipor-privacy-policy.component';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    RouterLink,
    MotifThemeModule,
    MsalModule,
    CommonModule,
    MotifModalModule,
    SideNavComponent,
    HeaderComponent,
    ProgressLoaderComponent,
    DataLoaderComponent,
    TimeoutDialogComponent,
    LogoutComponent,
    TranslatePipe,
    TranslateDirective,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  private translate = inject(TranslateService);
  showMenu: boolean = false;
  currentOption: number = 0;
  isLogin: boolean = false;
  loginDisplay = false;
  loginSuccess = false;
  isloggedIn: boolean = false;
  isLoggedOut: boolean = false;
  user: any;
  popUpHeadline: string = '';
  popUpDescription: string = '';
  userInput: CreateUserCommand;
  isDashboard = false;
  isLanding = true;
  private readonly _destroying$ = new Subject<void>();
  theme: string;
  hideMenu: boolean = true;
  url: string;
  isPmproLoaded: boolean = true;
  className: string = '';
  isMenuToggled: boolean = false;
  private hasBootstrappedToken = false;

  private readonly validModules = [
    ApplicationConstants.PMPRO,
    ApplicationConstants.IPOR,
  ];

  constructor(
    private readonly hideShowScreenService: HideShowScreenService,
    @Inject(MSAL_GUARD_CONFIG)
    private readonly msalGuardConfig: MsalGuardConfiguration,
    private readonly userService: UserService,
    private readonly commonService: CommonService,
    private readonly authService: MsalService,
    private readonly msalBroadcastService: MsalBroadcastService,
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly appInsightsService: AppInsightsService,
    private readonly storageService: SecureStorageService,
    private readonly cacheService: CacheService,
    public readonly modal: MotifModal,
    private readonly notificationService: NotificationService,
  ) {
    localStorage.setItem('privacyNotice', 'false');
    this.initializeTelemetry();

    this.router.events.subscribe({
      next: (event) => {
        if (event instanceof NavigationStart) {
          this.isDashboard = event.url.includes('/dashboard');
          this.isLanding = event.url == '/';
          this.url = event.url;
          if (event.url.includes('/logout')) {
            this.isLogin = false;
            this.isLoggedOut = true;
            return;
          } else {
            this.isLogin = true;
            this.isLoggedOut = false;
          }
          this.getClassName();
        }
      },
    });

    this.commonService.isPMproApplicationSelected.subscribe((isPmpro) => {
      this.isPmproLoaded = isPmpro;
      this.updateDocumentTitle(
        isPmpro ? ApplicationConstants.PMPRO : ApplicationConstants.IPOR,
      );
      this.getClassName();
    });

    if (this.storageService.getLocalStorageData('theme')) {
      this.theme = this.storageService.getLocalStorageData('theme');
    }

    if (this.theme == null) {
      this.storageService.saveLocalStorageData('selectedtheme', 'auto');
      this.theme = globalThis.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
    }

    // this.translate.setDefaultLang('en');
    // this.translate.setFallbackLang('en');

    if (this.storageService.getSessionStorageData('selectedLanguage')) {
      this.translate
        .use(this.storageService.getSessionStorageData('selectedLanguage'))
        .subscribe(() => {});
    } else {
      const browserLang = this.translate.getBrowserLang();
      this.translate.use(
        ['en', 'fr'].includes(browserLang) ? browserLang : 'en',
      );
    }

    this.translate.onLangChange.subscribe((event) => {});
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
    localStorage.setItem('privacyNotice', 'false');
  }

  getClassName(): void {
    if (this.isDashboard) {
      this.className = 'pl_5';
    } else if (this.isLanding) {
      this.className = 'p-landing';
    } else if (!this.isDashboard && !this.isLanding) {
      this.className = 'p-main';
    }
  }

  ngOnInit() {
    this.applyModuleFromRouteParams();
    this.updateDocumentTitle();

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this._destroying$),
      )
      .subscribe(() => {
        this.applyModuleFromRouteParams();
        this.updateDocumentTitle();
      });

    this.hideShowScreenService.menuVisible$
      .pipe(takeUntil(this._destroying$))
      .subscribe((visible) => {
        this.hideMenu = visible;
      });
    this.applyTheme();
    this.commonService.themeChange$
      .pipe(takeUntil(this._destroying$))
      .subscribe((theme: string) => {
        this.switchTheme(theme);
      });
    this.appInsightsService.logPageView('App Loaded', globalThis.location.href);
    this.authService
      .handleRedirectObservable()
      .pipe(takeUntil(this._destroying$))
      .subscribe({
        next: (result) => {
          if (result?.account) {
            this.authService.instance.setActiveAccount(result.account);
          }
          this.completeLoginBootstrap();
        },
        error: (error) => {
          console.error('MSAL redirect handling error:', error);
          this.completeLoginBootstrap();
        },
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            // msg.eventType === EventType.SSO_SILENT_SUCCESS ||
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS,
        ),
      )
      .subscribe({
        next: (result: EventMessage) => {
          const payload = result.payload as AuthenticationResult;
          this.authService.instance.setActiveAccount(payload.account);
          this.completeLoginBootstrap();
        },
      });
    //to set login completed flag once all the interactions are complete
    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None,
        ),
        takeUntil(this._destroying$),
      )
      .subscribe({
        next: () => {
          this.completeLoginBootstrap();
        },
      });
  }

  private completeLoginBootstrap(): void {
    this.setLoginDisplay();
    if (!this.loginDisplay || this.hasBootstrappedToken) {
      return;
    }

    this.hasBootstrappedToken = true;
    this.generateToken();
  }

  openPrivacyPolicy(userId) {
    let modalRef;
    const modalConfig = {
      size: ModalSizes.XL,
      slideout: false,
      disableClose: true,
      width: '50vw',
    };
    if (this.isPmproLoaded) {
      modalRef = this.modal.open(PrivacyComponentComponent, modalConfig);
    } else {
      modalRef = this.modal.open(IporPrivacyPolicyComponent, modalConfig);
    }

    modalRef.afterClosed().subscribe((result) => {
      if (result.response) {
        const updateUserRequest: UpdateUserCommandRequest = {
          IsPrivacyPolicyAccepted: true,
        };

        this.userService.updateUser(userId, updateUserRequest).subscribe({
          next: (response) => {
            console.log('User updated successfully:');
            this.loginSuccess = true;
          },
          error: (error) => {
            console.error('Error updating user:', error);
          },
        });
      } else {
        this.router.navigateByUrl('/logout');
      }
    });
  }

  private initializeTelemetry(): void {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.appInsightsService.logPageView(
          'Page View',
          event.urlAfterRedirects,
        );
      }
    });
  }

  generateToken() {
    this.authService
      .acquireTokenSilent({ scopes: environment.msalConfig.auth.apiscopes })
      .subscribe({
        next: (tokenResponse) => {
          const accessToken = tokenResponse.accessToken;
          if (!accessToken) {
            console.error('Access token is null or empty');
            return;
          }
          this.commonService.startWatching();
          this.storageService.saveSessionStorageData(
            'accessToken',
            accessToken,
          );
          this.storageService.saveSessionStorageData(
            'loginTimestamp',
            Date.now().toString(),
          );
          this.timeoutlogics();
          this.loadUserProfile();
        },
        error: (error) => {
          console.error('Token acquisition error:', error);
          this.authService.loginRedirect();
        },
      });
  }

  loadUserProfile(isToggled: boolean = false) {
    this.authService
      .acquireTokenSilent({ scopes: environment.msalConfig.auth.scopes })
      .subscribe({
        next: (tokenResponse) => {
          const accessToken = tokenResponse.accessToken;
          if (!accessToken) {
            console.error('Access token is null or empty');
            return;
          }
          this.storageService.saveSessionStorageData(
            'graphAccessToken',
            accessToken,
          );
          this.fetchUserProfile(accessToken, isToggled);
        },
        error: (error) => {
          console.error('Token acquisition error:', error);
          this.authService.loginRedirect();
        },
      });
  }

  timeoutlogics() {
    this.commonService.logOut().subscribe((item) => {
      if (item) {
        this.commonService.stopWatching();
        this.openResumeModal(false, false);
        globalThis.location.href = '/logout';
      }
    });
    this.commonService.idleCheck().subscribe((item) => {
      this.popUpHeadline = 'SessionTimeoutTitle';
      this.popUpDescription = 'SessionTimeoutMessage';
      if (item)
        this.openResumeModal(
          true,
          item,
          environment.timeoutValues.IDLE_TIMEOUT_INSECONDS,
        );
    });

    this.commonService.forceLogOut().subscribe((item) => {
      this.popUpHeadline = 'LongActiveSessionTitle';
      this.popUpDescription = 'LongActiveSessionMessage';
      if (item)
        this.openResumeModal(
          true,
          false,
          environment.timeoutValues.REMINDERTIME_IN_MINUTES,
        );
    });
  }

  openResumeModal(action: boolean, show: boolean, timeOut: number = 0): void {
    if (action) {
      this.openModal(show, timeOut);
    } else {
      this.modal.closeAll();
    }
  }

  private openModal(show: boolean, timeOut: number): void {
    const modalRef = this.modal.open(TimeoutDialogComponent, {
      size: ModalSizes.L,
      slideout: false,
      disableClose: true,
      data: {
        title: this.popUpHeadline,
        description: this.popUpDescription,
        timeout: timeOut,
        okButtonText: 'Ok',
        continueButtonText: 'ResumeSession',
        show: show,
      },
    });

    modalRef.afterClosed().subscribe((result) => {
      if (result) {
        this.resumeSessionClick();
      } else {
        this.openResumeModal(false, false);
      }
    });
  }

  resumeSessionClick() {
    this.openResumeModal(false, false);
    this.commonService.startWatching();
  }

  fetchUserProfile(accessToken: string, isToggled: boolean = false) {
    this.http
      .get(environment.msalConfig.auth.uri, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .subscribe({
        next: (profile) => {
          this.user = profile;
          const email = (
            this.user.mail ||
            this.user.userPrincipalName ||
            ''
          ).toLowerCase();
          const domain = email.split('@')[1] || '';
          const eyDomains = ['ey.com', 'ey.net'];
          let isEYUser = eyDomains.some((d) => domain.endsWith(d));
          let initials = this.commonService.getInitials(this.user);
          this.userService.setUser(profile);
          this.userInput = {
            userId: this.user.id,
            displayName: this.user.displayName,
            firstName: this.user.givenName,
            lastName: this.user.surname,
            email: this.user.mail,
            jobTitle: this.user.jobTitle,
            phone: this.user.businessPhones[0],
            isActive: true,
            initials: initials,
            isEYUser: isEYUser,
          };
          this.userService.registerUser(this.userInput).subscribe({
            next: (response) => {
              if (response.success) {
                this.storageService.saveSessionStorageData(
                  'userInfo',
                  response.Data.UserDto,
                );
                this.storageService.saveSessionStorageData(
                  'roleInfo',
                  response.Data.Roles,
                );
                this.storageService.saveSessionStorageData(
                  'projectRoleInfo',
                  response.Data.UserProjectRoles,
                );
                this.storageService.saveSessionStorageData(
                  'permissionInfo',
                  response.Data.Permissions ?? [],
                );
                this.isMenuToggled = isToggled;
                let isPrivacyPolicyAccepted =
                  response.Data.UserDto.IsPrivacyPolicyAccepted;
                if (isPrivacyPolicyAccepted) {
                  this.loginSuccess = true;
                  this.router.navigateByUrl(this.url || '');
                } else {
                  this.openPrivacyPolicy(response.Data.UserDto.Id);
                }
                const userSettings = response.Data.UserSettings;
                if (userSettings && userSettings.PreferredDateFormat) {
                  this.commonService.applicationDateFormat =
                    userSettings.PreferredDateFormat;
                } else {
                  this.commonService.applicationDateFormat =
                    ApplicationConstants.SYNCFUSION_DATE_FORMAT;
                }
                this.notificationService.getUnreadCount().subscribe();
              }
            },
            error: (error) => {
              console.error('Error while inserting user:', error);
            },
          });
          setTimeout(() => {
            this.fetchUserImage(accessToken);
          }, 0);
        },
        error: (error) => console.error('Graph API error:', error),
      });
  }

  fetchUserImage(accessToken: string) {
    const cacheKey = `${this.userInput.email}_currentUserProfileImage`;
    const cacheMinutes =
      ApplicationConstants.ProfilePictureCacheExpiryInMinutes;

    const setImageAndCache = (base64Image: string) => {
      this.userService.setUserImage(base64Image);
      this.cacheService.setIndexedDB(
        cacheKey,
        base64Image,
        cacheMinutes * 60 * 1000,
      );
    };

    const fetchAndCacheImage = () => {
      this.http
        .get(environment.msalConfig.auth.profileImageUri, {
          headers: { Authorization: `Bearer ${accessToken}` },
          responseType: 'blob',
        })
        .subscribe({
          next: (imageBlob: Blob) => {
            this.commonService
              .convertBlobToBase64(imageBlob)
              .then(setImageAndCache)
              .catch((error) => {
                console.error('Error converting Blob to Base64:', error);
              });
          },
          error: (error) => {
            console.error('Graph API error:', error);
            setImageAndCache('');
          },
        });
    };

    this.cacheService
      .getIndexedDB<string>(cacheKey)
      .then((dbValue) => {
        if (dbValue === null) {
          fetchAndCacheImage();
        } else {
          this.cacheService.setMemory(cacheKey, dbValue);
          this.userService.setUserImage(dbValue);
        }
      })
      .catch((error) => {
        console.error('Error accessing IndexedDB:', error);
        fetchAndCacheImage();
      });
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

  switchLanguage(language: string) {
    this.translate.use(language);
  }

  switchTheme(theme: string) {
    this.storageService.saveLocalStorageData('theme', theme);
    this.theme = theme;
    this.commonService.setTheme(theme);
    this.applyTheme();
  }

  applyTheme() {
    const body = document.body;
    if (this.theme === 'light') {
      body.classList.add('light-theme');
      body.classList.remove('dark-theme');
    } else {
      body.classList.add('dark-theme');
      body.classList.remove('light-theme');
    }
  }

  handleMenuToggle(showMenu: boolean): void {
    this.showMenu = showMenu;
  }

  handleProjectToggle(projectVal: string): void {
    this.isPmproLoaded = projectVal === 'pmpro';
    this.updateDocumentTitle(projectVal);
    this.isMenuToggled = false;
    this.loadUserProfile(true);
  }

  logout() {
    this.commonService.setActiveModuleinLocalStorage();
    this.authService.logoutRedirect({
      postLogoutRedirectUri: environment.msalConfig.auth.redirectUri,
    });
  }

  private applyModuleFromRouteParams(): void {
    const module = this.readModuleFromRouteParams();
    if (!module || !this.validModules.includes(module)) {
      this.updateDocumentTitle();
      return;
    }

    this.storageService.saveSessionStorageData('activeModule', module);
    this.storageService.saveLocalStorageData('module', module);
    this.isPmproLoaded = module === ApplicationConstants.PMPRO;
    this.commonService.isPMproApplicationSelected.next(this.isPmproLoaded);
    this.updateDocumentTitle(module);
  }

  private updateDocumentTitle(module?: string): void {
    const normalizedModule = (
      module ||
      this.storageService.getSessionStorageData('activeModule') ||
      (this.isPmproLoaded
        ? ApplicationConstants.PMPRO
        : ApplicationConstants.IPOR)
    ).toLowerCase();

    const moduleLabel =
      normalizedModule === ApplicationConstants.IPOR ? 'IPO Ready' : 'PM Pro';
    document.title = moduleLabel;
  }

  private readModuleFromRouteParams(): string | null {
    let routeSnapshot: ActivatedRouteSnapshot | null =
      this.router.routerState.snapshot.root;

    while (routeSnapshot) {
      const module = routeSnapshot.paramMap.get('module');

      if (module) {
        return module.toLowerCase();
      }

      routeSnapshot = routeSnapshot.firstChild;
    }

    return null;
  }
}
